import { memo } from "react";
import { ArrowTopRightOnSquareIcon, Card } from "@";
import ScriptField from "./ScriptField";

/**
 * Tracking-service card — thin wrapper around the shared <Card />.
 * Provides:
 *  - brand mark in the `leading` slot
 *  - live "configured / not configured" pill in the `badge` slot
 *  - tagline in the `description` slot
 *  - docs link + script fields in the body
 *
 * @param {{
 *   serviceKey: string,
 *   name: string,
 *   tagline: string,
 *   accent: { bg: string, text: string, ring: string, dot: string },
 *   brandMark: React.ReactNode,
 *   docsHref: string,
 *   docsLabel?: string,
 *   fields: Array<{ name: string, label: string, hint?: string, placeholder?: string, rows?: number }>,
 *   register: any,
 *   watch: any,
 *   errors: any,
 *   defaultOpen?: boolean,
 * }} props
 */
const ServiceCard = ({
  name,
  tagline,
  accent,
  brandMark,
  docsHref,
  docsLabel = "كيف أحصل على الشيفرة؟",
  fields,
  register,
  watch,
  errors,
  defaultOpen = false,
}) => {
  // Service is "configured" if ANY of its fields has content
  const configured = fields.some(
    (f) => (watch(f.name) || "").trim().length > 0,
  );

  const brandChip = (
    <span
      className={`flex h-11 w-11 items-center justify-center rounded-2xl ring-1 ${accent.ring} ${accent.bg} ${accent.text}`}
    >
      {brandMark}
    </span>
  );

  const statusPill = configured ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-800/50">
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      مُفعَّل
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:border-gray-600 dark:text-gray-500">
      غير مُكوَّن
    </span>
  );

  return (
    <Card
      title={name}
      description={tagline}
      leading={brandChip}
      badge={statusPill}
      isOpen={defaultOpen}
    >
      {/* Docs link */}
      {docsHref && (
        <a
          href={docsHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-[11.5px] font-semibold text-purple-900 ring-1 ring-purple-100 transition-colors hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40 dark:hover:bg-purple-900/40"
        >
          {docsLabel}
          <ArrowTopRightOnSquareIcon className="h-3 w-3" strokeWidth={2.25} />
        </a>
      )}

      {/* Fields */}
      <div className="grid gap-4 md:grid-cols-2">
        {fields.map((field) => (
          <div
            key={field.name}
            className={fields.length === 1 ? "md:col-span-2" : ""}
          >
            <ScriptField
              label={field.label}
              hint={field.hint}
              placeholder={field.placeholder}
              rows={field.rows || 6}
              input={register(field.name)}
              value={watch(field.name)}
              error={errors?.[field.name]?.message}
            />
          </div>
        ))}
      </div>
    </Card>
  );
};

export default memo(ServiceCard);
